/**
 * Clean Architecture Station Thunks
 *
 * Uses domain use-cases and infrastructure adapters instead of
 * directly calling AtcManifestService. Part of the strangler migration pattern.
 *
 * Strangler Pattern:
 * - These thunks run alongside legacy thunks in atcSlice.ts
 * - Feature flag controls which path is used
 * - Both paths produce identical Redux state shape
 * - Once verified, legacy path will be removed
 *
 * Clean Architecture:
 * - Thunks are in the interface adapter layer
 * - They coordinate use-cases and Redux (presentation)
 * - Data flows: Use-Case → Domain Entities → Mapper → Legacy Types → Redux
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '@store/index';
import { AtcStation } from '@/types/AtcManifest';
import { Logger } from '@/app/utils/logger';
import {
  getPerformanceTimingService,
  PERFORMANCE_MARKS,
} from '@/app/services/performance/PerformanceTimingService';
import {
  FetchStationsUseCaseImpl,
  SelectStationUseCaseImpl,
} from '@/infrastructure/use-cases';
import { getAtcStationAdapter } from '@/infrastructure/adapters';
import { StationMapper } from '@/infrastructure/mappers';
import type { Station as DomainStation } from '@/domain/entities';

const LOG_NAMESPACE = 'clean-station-thunk';

/**
 * Data source types for SWR tracking
 */
export type StationsDataSource = 'initial' | 'cache' | 'network' | 'background';

/**
 * Fetch options for SWR pattern
 */
export interface FetchStationsOptions {
  forceRefresh?: boolean;
  background?: boolean;
}

/**
 * Create FetchStations use-case instance
 */
function createFetchStationsUseCase(): FetchStationsUseCaseImpl {
  const adapter = getAtcStationAdapter();
  return new FetchStationsUseCaseImpl(adapter);
}

/**
 * Create SelectStation use-case instance
 */
function createSelectStationUseCase(): SelectStationUseCaseImpl {
  const adapter = getAtcStationAdapter();
  return new SelectStationUseCaseImpl(adapter);
}

export async function loadStationScenariosWithCleanArchitecture(
  station: AtcStation,
  forceRefresh = false
): Promise<AtcStation> {
  const domainStation: DomainStation = StationMapper.toDomainStation(station);
  const useCase = createSelectStationUseCase();
  const result = await useCase.execute({ station: domainStation, forceRefresh });
  return StationMapper.toLegacyStation(result.station);
}

/**
 * Fetch stations using clean architecture
 *
 * Flow:
 * 1. Create FetchStationsUseCase with AtcStationAdapter
 * 2. Execute use-case (handles caching internally)
 * 3. Map domain Stations back to legacy format for Redux
 *
 * Supports SWR pattern:
 * - When background=true, returns result without affecting loading state
 * - Returns early if background refresh already in progress
 */
export const cleanFetchStations = createAsyncThunk(
  'atc/cleanFetchStations',
  async (
    options: FetchStationsOptions | boolean = false,
    { rejectWithValue, getState }
  ) => {
    // Normalize options (backward compatibility: boolean = forceRefresh)
    const opts: FetchStationsOptions =
      typeof options === 'boolean'
        ? { forceRefresh: options, background: false }
        : options;

    const { forceRefresh = false, background = false } = opts;

    // Check if background refresh is already in progress
    const state = getState() as RootState;
    if (background && state.atc.stationsBackgroundRefreshing) {
      Logger.debug('Background refresh already in progress, skipping', LOG_NAMESPACE);
      return { stations: state.atc.stations, isBackground: true, skipped: true };
    }

    try {
      Logger.info(
        `Fetching stations via clean architecture (forceRefresh=${forceRefresh}, background=${background})`,
        LOG_NAMESPACE
      );

      const useCase = createFetchStationsUseCase();
      const result = await useCase.execute({ forceRefresh });

      // Map domain stations to legacy format for Redux
      const legacyStations = result.stations.map((s) => StationMapper.toLegacyStation(s));

      Logger.info(
        `Stations fetched via clean architecture: ${legacyStations.length} stations`,
        LOG_NAMESPACE
      );

      // Mark ATC stations ready for performance tracking (only on initial load)
      if (!background) {
        getPerformanceTimingService().mark(PERFORMANCE_MARKS.ATC_STATIONS_READY);
      }

      return { stations: legacyStations, isBackground: background, skipped: false };
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      Logger.error(`Clean fetch stations failed: ${message}`, LOG_NAMESPACE);
      return rejectWithValue(message);
    }
  }
);

/**
 * Select a station using clean architecture
 *
 * Flow:
 * 1. Convert legacy AtcStation to domain Station
 * 2. Create SelectStationUseCase with AtcStationAdapter
 * 3. Execute use-case (loads scenarios)
 * 4. Map domain Station back to legacy format for Redux
 */
export const cleanSelectStation = createAsyncThunk(
  'atc/cleanSelectStation',
  async (station: AtcStation, { rejectWithValue }) => {
    try {
      Logger.info(
        `Selecting station via clean architecture: ${station.displayName}`,
        LOG_NAMESPACE
      );

      const legacyStation = await loadStationScenariosWithCleanArchitecture(station, false);

      Logger.info(
        `Station selected via clean architecture: ${legacyStation.displayName}, ${legacyStation.scenarios?.length ?? 0} scenarios`,
        LOG_NAMESPACE
      );

      return legacyStation;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      Logger.error(`Clean select station failed: ${message}`, LOG_NAMESPACE);
      return rejectWithValue(message);
    }
  }
);

/**
 * Refresh station scenarios using clean architecture (force network fetch)
 *
 * Used as a recovery mechanism when an ATC scenario URL 404s.
 */
export const cleanRefreshStationScenarios = createAsyncThunk(
  'atc/cleanRefreshStationScenarios',
  async (station: AtcStation, { rejectWithValue }) => {
    try {
      Logger.info(
        `Refreshing station scenarios via clean architecture: ${station.displayName}`,
        LOG_NAMESPACE
      );

      const legacyStation = await loadStationScenariosWithCleanArchitecture(station, true);

      Logger.info(
        `Station scenarios refreshed via clean architecture: ${legacyStation.displayName}, ${legacyStation.scenarios?.length ?? 0} scenarios`,
        LOG_NAMESPACE
      );

      return legacyStation;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      Logger.error(`Clean refresh station scenarios failed: ${message}`, LOG_NAMESPACE);
      return rejectWithValue(message);
    }
  }
);

/**
 * Auto-select first available station using clean architecture
 */
export const cleanAutoSelectFirstStation = createAsyncThunk(
  'atc/cleanAutoSelectFirstStation',
  async (_: void, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as RootState;

      if (state.atc.selectedStation) {
        Logger.info('Auto-select skipped; station already selected', LOG_NAMESPACE);
        return state.atc.selectedStation;
      }

      const stations = state.atc.stations;

      if (!stations || stations.length === 0) {
        Logger.warn('Auto-select skipped; no stations available', LOG_NAMESPACE);
        return null;
      }

      const firstStation = stations[0];
      const stationWithScenarios = await dispatch(cleanSelectStation(firstStation)).unwrap();
      return stationWithScenarios;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      Logger.error(`Clean auto-select station failed: ${message}`, LOG_NAMESPACE);
      return rejectWithValue(message);
    }
  }
);

/**
 * Select station by airport code and station type using clean architecture
 */
export const cleanSelectStationByCode = createAsyncThunk(
  'atc/cleanSelectStationByCode',
  async (
    { airportCode, stationType }: { airportCode: string; stationType: string },
    { getState, dispatch, rejectWithValue }
  ) => {
    try {
      const state = getState() as RootState;
      let stations = state.atc.stations;

      // If stations not loaded, fetch them first
      if (stations.length === 0) {
        const result = await dispatch(cleanFetchStations({ forceRefresh: false })).unwrap();
        stations = result.stations;
      }

      // Find the matching station
      const station = stations.find(
        (s: AtcStation) =>
          s.airportCode.toLowerCase() === airportCode.toLowerCase() &&
          s.stationType.toLowerCase() === stationType.toLowerCase()
      );

      if (!station) {
        throw new Error(`Station not found: ${airportCode} ${stationType}`);
      }

      // Select the station (this will load scenarios)
      const stationWithScenarios = await dispatch(cleanSelectStation(station)).unwrap();
      return stationWithScenarios;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      Logger.error(`Clean select station by code failed: ${message}`, LOG_NAMESPACE);
      return rejectWithValue(message);
    }
  }
);
